import {
  LiveAnswersClient,
  LiveAnswersMessage,
} from '@stripe-internal/conversations-client';
import {LiveAnswersPoller, LiveAnswersPollerState} from './LiveAnswersPoller';
import {StreamingBuffer} from './StreamingBuffer';

export interface LiveAnswersStreamerState extends LiveAnswersPollerState {
  isDoneStreaming: boolean;
  content: string;
  isStreaming: boolean;
}

const BUFFER_FLUSH_INTERVAL_MS = 20;
const BUFFER_CHUNK_SIZE = 5;

/**
 * Wraps LiveAnswersPoller to provide streaming content display.
 * Polls for message updates and streams the content character-by-character
 * for a typewriter effect.
 */
export class LiveAnswersStreamer {
  private _poller: LiveAnswersPoller;

  private _state: LiveAnswersStreamerState;

  private _listeners = new Set<() => void>();

  private _buffer: StreamingBuffer | null = null;

  private _unsubscribeFromPoller: (() => void) | null = null;

  private _lastPushedLength = 0;

  constructor(client: LiveAnswersClient, message: LiveAnswersMessage) {
    this._poller = new LiveAnswersPoller(client, message);

    const pollerState = this._poller.getState();

    let content = '';
    let isDoneStreaming = false;

    if (pollerState.isComplete) {
      content = pollerState.message.data?.content || '';
      isDoneStreaming = true;
      this._lastPushedLength = content.length;
    }

    this._state = {
      ...pollerState,
      content,
      isDoneStreaming,
      isStreaming: false,
    };
  }

  public getState(): LiveAnswersStreamerState {
    return this._state;
  }

  public subscribe(callback: () => void): () => void {
    this._listeners.add(callback);
    return () => {
      this._listeners.delete(callback);
    };
  }

  public startStreaming() {
    if (this._state.isDoneStreaming) {
      return;
    }

    this._unsubscribeFromPoller = this._poller.subscribe(() => {
      this.handlePollerUpdate();
    });

    this._buffer?.cancel();
    this._buffer = new StreamingBuffer({
      chunkSize: BUFFER_CHUNK_SIZE,
      flushIntervalMs: BUFFER_FLUSH_INTERVAL_MS,
      onFlush: (content: string) => {
        this.setState({content, isStreaming: true});
      },
      onIdle: () => {
        this.setState({isStreaming: false});
      },
      onComplete: (content: string) => {
        this.setState({content, isDoneStreaming: true, isStreaming: false});
      },
    });

    this._poller.startPolling();
  }

  public stopStreaming() {
    this._poller.stopPolling();
    this._buffer?.cancel();
    this._unsubscribeFromPoller?.();
    this._unsubscribeFromPoller = null;
  }

  private setState(newState: Partial<LiveAnswersStreamerState>) {
    this._state = {
      ...this._state,
      ...newState,
    };
    this._listeners.forEach((callback) => callback());
  }

  private handlePollerUpdate() {
    const pollerState = this._poller.getState();

    this._state = {
      ...this._state,
      isCancelled: pollerState.isCancelled,
      isComplete: pollerState.isComplete,
      error: pollerState.error,
      message: pollerState.message,
    };

    if (pollerState.isCancelled) {
      this._buffer?.cancel();
      this.setState({content: '', isDoneStreaming: true, isStreaming: false});
      return;
    }

    if (pollerState.error) {
      this._buffer?.cancel();
      this.setState({isDoneStreaming: true, isStreaming: false});
      return;
    }

    const messageContent = pollerState.message.data?.content || '';

    if (messageContent.length > this._lastPushedLength) {
      const newContent = messageContent.slice(this._lastPushedLength);
      this._lastPushedLength = messageContent.length;
      this._buffer?.push(newContent);
    }

    if (pollerState.isComplete) {
      this._buffer?.complete();
    }

    this._listeners.forEach((callback) => callback());
  }

  public destroy() {
    this.stopStreaming();
    this._poller.destroy();
    this._listeners.clear();
  }
}
